import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { cn } from "@/lib/classNames";

type ToastVariant = "info" | "success" | "error";

interface ToastOptions {
  title?: string;
  description: string;
  variant?: ToastVariant;
  durationMs?: number | null;
}

interface ToastRecord extends Required<Pick<ToastOptions, "description">> {
  id: string;
  title?: string;
  variant: ToastVariant;
  durationMs: number | null;
}

interface ToastApi {
  toast: (options: ToastOptions) => string;
  info: (description: string, options?: Omit<ToastOptions, "description" | "variant">) => string;
  success: (description: string, options?: Omit<ToastOptions, "description" | "variant">) => string;
  error: (description: string, options?: Omit<ToastOptions, "description" | "variant">) => string;
  dismiss: (id: string) => void;
}

const DEFAULT_DURATION_MS = 4500;
const ERROR_DURATION_MS = 6500;

const ToastContext = createContext<ToastApi | null>(null);

function buildToastId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }

  return `toast-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function getToastPalette(variant: ToastVariant): string {
  if (variant === "success") {
    return "border-neutral-950 bg-[#3e664d] text-[#fff7ec]";
  }

  if (variant === "error") {
    return "border-[#7a1d16] bg-[#fff1ea] text-[#52110d]";
  }

  return "border-neutral-950 bg-[#fff8ea] text-neutral-950";
}

function getToastEyebrow(variant: ToastVariant): string {
  if (variant === "success") {
    return "Saved";
  }

  if (variant === "error") {
    return "Problem";
  }

  return "Notice";
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const timeoutMapRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: string) => {
    const timeoutId = timeoutMapRef.current.get(id);
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutMapRef.current.delete(id);
    }

    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const toast = useCallback(
    ({ title, description, variant = "info", durationMs }: ToastOptions): string => {
      const id = buildToastId();
      const resolvedDurationMs =
        durationMs === undefined ? (variant === "error" ? ERROR_DURATION_MS : DEFAULT_DURATION_MS) : durationMs;

      setToasts((current) => [...current, { id, title, description, variant, durationMs: resolvedDurationMs }]);

      if (resolvedDurationMs !== null) {
        const timeoutId = setTimeout(() => {
          dismiss(id);
        }, resolvedDurationMs);
        timeoutMapRef.current.set(id, timeoutId);
      }

      return id;
    },
    [dismiss],
  );

  useEffect(() => {
    return () => {
      for (const timeoutId of timeoutMapRef.current.values()) {
        clearTimeout(timeoutId);
      }
      timeoutMapRef.current.clear();
    };
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      toast,
      info: (description, options) => toast({ ...options, description, variant: "info" }),
      success: (description, options) => toast({ ...options, description, variant: "success" }),
      error: (description, options) => toast({ ...options, description, variant: "error" }),
      dismiss,
    }),
    [dismiss, toast],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="false"
        className="pointer-events-none fixed right-4 bottom-4 z-200 flex w-[min(26rem,calc(100vw-2rem))] flex-col gap-3"
      >
        {toasts.map((entry) => (
          <section
            key={entry.id}
            role={entry.variant === "error" ? "alert" : "status"}
            className={cn(
              "pointer-events-auto relative overflow-hidden border-[3px] px-4 py-4 shadow-[10px_10px_0_rgba(19,19,19,0.18)]",
              getToastPalette(entry.variant),
            )}
          >
            <div
              className={cn(
                "absolute inset-x-0 top-0 h-2",
                entry.variant === "success"
                  ? "bg-[linear-gradient(90deg,rgba(255,255,255,0.22),transparent)]"
                  : "bg-[linear-gradient(90deg,rgba(255,255,255,0.55),transparent)]",
              )}
            />
            <div className="flex items-start gap-4">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-semibold uppercase tracking-[0.32em] opacity-70">
                  {entry.title ?? getToastEyebrow(entry.variant)}
                </p>
                <p className="mt-2 text-sm font-semibold leading-6">{entry.description}</p>
              </div>
              <button
                type="button"
                onClick={() => dismiss(entry.id)}
                className="shrink-0 border-2 border-current px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] transition-opacity hover:opacity-75"
                aria-label="Dismiss notification"
              >
                Close
              </button>
            </div>
          </section>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error("useToast must be used within a ToastProvider.");
  }

  return context;
}
